import { useInfiniteQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useProfitPoolOverviewContext } from '../context/ProfitPoolOverviewContext';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from '../apiUtils';
import { isEmpty } from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';

const useProfitGrowthByHierarchy = () => {
    const { selectedSubFilter, selectPPGrowthUnit } = useProfitPoolOverviewContext();
    const {
        singleSelectedTier,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        presetsFilterObj,
    } = useContext(FilterContextData);

    const [profitGrowthData, setProfitGrowthData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    const { data, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: [
            'profit-growth-by-hierarchy-infi',
            selectedSubFilter?.dataKey,
            selectPPGrowthUnit,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                limit: PROFIT_POOL_API_LIMIT,
                kpi: selectPPGrowthUnit,
                tierList: singleSelectedTier as string[],
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                allDimAndAttributeTypeList.loading === false &&
                !timePeriodLoading &&
                timePeriodSelection &&
                !idForRestOfComponentLoading &&
                presetsFilterObj !== undefined,
        ),
    });

    useEffect(() => {
        if (!isPending && data?.pages) {
            const allData = data.pages.flatMap((page: any) => page?.data || []);
            if (!isEmpty(allData)) {
                let minGrowth = Number.POSITIVE_INFINITY;
                let maxGrowth = Number.NEGATIVE_INFINITY;

                const finalChartData = allData.map((item: any) => {
                    const currentProfitGrowth = item.kpi_values?.[selectPPGrowthUnit] || 0;
                    if (currentProfitGrowth < minGrowth) minGrowth = currentProfitGrowth;
                    if (currentProfitGrowth > maxGrowth) maxGrowth = currentProfitGrowth;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        profit_growth: currentProfitGrowth,
                        trend: currentProfitGrowth > 0 ? '#42A531' : '#FF4D4F',
                        tier: item.tier,
                    };
                });
                setProfitGrowthData(finalChartData);
                setMin(minGrowth === Number.POSITIVE_INFINITY ? 0 : minGrowth);
                setMax(maxGrowth === Number.NEGATIVE_INFINITY ? 0 : maxGrowth);
            } else {
                setProfitGrowthData([]);
                setMin(0);
                setMax(0);
            }
        }
    }, [isPending, data]);

    return {
        data: profitGrowthData,
        min,
        max,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    };
};

export default useProfitGrowthByHierarchy;
